import { api } from './api'

export interface Provider {
  id: string
  companyName?: string
  serviceType: string
  description?: string
  address?: string
  city?: string
  state?: string
  rating?: number
  reviewCount?: number
  isActive: boolean
  // Additional fields from smart matching
  distance?: number
  avgRating?: number
  totalScore?: number
  estimatedArrival?: string
  availableSlots?: number
  canPerformJanazah?: boolean
  expertise?: string[]
  availablePlots?: number
  muslimSectionAvailable?: boolean
  pricing?: any
  facilities?: string[]
}

export interface ProviderMatchResult {
  providers: Provider[]
  searchLocation: string
  totalProviders: number
  matchedCount: number
}

export interface ComprehensiveMatches {
  ambulance: ProviderMatchResult
  ustadz: ProviderMatchResult
  burialGrounds: ProviderMatchResult
  recommendations: Recommendation[]
  estimatedCosts: EstimatedCosts
  urgencyLevel: string
  searchTimestamp: string
}

export interface Recommendation {
  type: 'ambulance' | 'ustadz' | 'burial'
  priority: 'high' | 'medium' | 'low'
  provider: string
  reason: string
  estimatedArrival?: string
  availability?: string
  location?: string
  action: string
}

export interface EstimatedCosts {
  ambulance: number
  ustadz: number
  burialGround: number
  subtotal: number
  estimatedAdditional: number
  total: number
  currency: string
}

export interface QuickAssessmentData {
  urgencyLevel: 'IMMEDIATE' | 'TODAY' | 'TOMORROW'
  locationType: 'HOSPITAL' | 'HOME' | 'MOSQUE' | 'OTHER'
  currentLocation: string
  preferredBurialArea: string
}

export interface QuickAssessmentResult {
  assessment: QuickAssessmentData
  recommendedPackages: any[]
  timeline: any
  providerMatches: {
    ambulances: Provider[]
    ustadz: Provider[]
    burialGrounds: Provider[]
  }
  nextSteps: string[]
}

class SmartMatchingService {
  async findAmbulanceMatches(params: {
    location: string
    urgencyLevel: string
    maxDistance?: number
    limit?: number
  }): Promise<ProviderMatchResult> {
    const response = await api.post('/smart-matching/ambulance', params)
    return response.data
  }

  async findUstadzMatches(params: {
    location: string
    requiredServices?: string[]
    maxDistance?: number
    limit?: number
  }): Promise<ProviderMatchResult> {
    const response = await api.post('/smart-matching/ustadz', params)
    return response.data
  }

  async findBurialGroundMatches(params: {
    preferredArea: string
    religiousRequirements?: string[]
    maxDistance?: number
    limit?: number
  }): Promise<ProviderMatchResult> {
    const response = await api.post('/smart-matching/burial-grounds', params)
    return response.data
  }

  async findComprehensiveMatches(params: {
    location: string
    urgencyLevel: string
    preferredBurialArea: string
    specialRequirements?: string[]
    selectedPackage?: {
      id: string
      name: string
      price: number
      includes: any[]
      excludes: any[]
    }
  }): Promise<ComprehensiveMatches> {
    const response = await api.post('/smart-matching/comprehensive', params)
    return response.data
  }

  async quickAmbulanceMatch(location: string, urgency: string): Promise<ProviderMatchResult> {
    const response = await api.get('/smart-matching/ambulance/quick', {
      params: { location, urgency }
    })
    return response.data
  }

  async quickUstadzMatch(location: string, services?: string): Promise<ProviderMatchResult> {
    const response = await api.get('/smart-matching/ustadz/quick', {
      params: { location, services }
    })
    return response.data
  }

  async quickBurialMatch(area: string, requirements?: string): Promise<ProviderMatchResult> {
    const response = await api.get('/smart-matching/burial/quick', {
      params: { area, requirements }
    })
    return response.data
  }

  async performQuickAssessment(assessmentData: QuickAssessmentData): Promise<QuickAssessmentResult> {
    const response = await api.post('/cases/quick-assessment', assessmentData)
    return response.data
  }

  async createCaseWithProviders(caseData: {
    deceasedData: any
    assessmentData: any
    selectedPackageId?: string
    customPackageConfig?: any
    providerMatchingData?: any
    selectedProviders?: any[]
    providersSkipped?: boolean
  }): Promise<any> {
    const response = await api.post('/public/create-case-with-providers', caseData)
    return response.data
  }

  async bookProviderForCase(caseId: string, bookingData: {
    providerId: string;
    serviceType: string;
    scheduledDateTime: string;
    duration?: number;
    location?: string;
    coordinates?: { lat: number; lng: number };
    specialRequests?: string;
    pricing?: any;
  }): Promise<any> {
    const response = await api.post(`/cases/${caseId}/book-provider`, bookingData)
    return response.data
  }

  async getCaseBookings(caseId: string): Promise<any> {
    const response = await api.get(`/cases/${caseId}/bookings`)
    return response.data
  }
}

export const smartMatchingService = new SmartMatchingService()